import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import { useBuyerActivityCompletion } from "components/buyer_activity/useBuyerActivityCompletion";
import CTACard from "components/ui/cta_card/CTACard";
import { useGenerateBuyerActivity } from "lib/hooks/api/buyer_activity/useGenerateBuyerActivity";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes, routeSegments } from "lib/routes";
import { BuyerActivityObjectType, BuyerActivityStatus } from "lib/types/graphQLEnums";
import { toHumanRelativeTimeFromNow } from "lib/utils/relative_time";
import { LeadLoadingRow, LeadSection } from "./LeadSection";

export function LeadReportsSection({ lead }: { lead: NonNullable<LeadDetails> }) {
  const navigate = useNavigate();
  const { mutate: generateBuyerActivityReport, isLoading: isGenerating } =
    useGenerateBuyerActivity();

  const summary = lead.buyerActivity;
  const isPending = summary?.status === BuyerActivityStatus.Pending;
  useBuyerActivityCompletion({
    status: summary?.status,
    notices: lead.notices ?? [],
    objectId: lead.id,
    objectType: BuyerActivityObjectType.Lead,
  });
  const reportRoute = `${routes.leads}/${lead.id}/${routeSegments.buyerActivity}`;
  const preview = reportPreview(summary?.questions ?? []);

  // Generating is only possible from the buyer's notice history, so without notices there's
  // nothing to research — same precondition the bid surface applies.
  const canGenerate = (lead.notices?.length ?? 0) > 0;

  if (!summary) {
    return (
      <LeadSection title="Reports">
        <CTACard
          title="Historic buyer activity"
          description="Learn more about this buyer to inform your qualification and approach to this deal."
          noIllustration
          buttonProps={{
            buttonText: "Generate report",
            icon: "plus",
            ariaLabel: "Generate buyer activity report",
            disabled: isGenerating || !canGenerate,
            isLoading: isGenerating,
            onClick: () =>
              generateBuyerActivityReport({
                GenerateBuyerActivitySummaryRequest: {
                  objectId: lead.id,
                  objectType: BuyerActivityObjectType.Lead,
                },
              }),
          }}
        />
      </LeadSection>
    );
  }

  return (
    <LeadSection title="Reports">
      {isPending ? (
        <LeadLoadingRow>Researching the buyer</LeadLoadingRow>
      ) : (
        <ReportLink
          type="button"
          aria-label="View buyer activity report"
          onClick={() => void navigate({ to: reportRoute })}
        >
          <ReportHeader>
            <UIcon icon="fileAttachment" size={16} color="sysTextSecondary" />
            <ReportName>Buyer activity report</ReportName>
            {summary.updatedAt && (
              <Text as="span" body color="sysTextSecondary">
                Updated {toHumanRelativeTimeFromNow(summary.updatedAt)}
              </Text>
            )}
          </ReportHeader>
          {preview && (
            <ReportPreview>
              <Text
                as="span"
                body
                color="sysTextSecondary"
                ellipsis
                ellipsisMethod="lineClamp"
                ellipsisLines={2}
              >
                {preview}
              </Text>
            </ReportPreview>
          )}
        </ReportLink>
      )}
    </LeadSection>
  );
}

/**
 * The opening of the report, as plain text for the card's two-line preview: the first answer with
 * its markdown (headings, bullets, emphasis, links) and `[n]` citation markers flattened away —
 * the report page renders those, a two-line clamp can't.
 */
function reportPreview(questions: { answer: string }[]): string | null {
  const answer = questions.map((q) => q.answer?.trim()).find(Boolean);
  if (!answer) return null;

  const text = answer
    // Envelope tags the backend is meant to strip, but leaves in place when it falls back to the
    // raw answer — same defensive removal AnswerMarkdown does.
    .replace(/\[SOURCES\][\s\S]*?\[\/SOURCES\]/g, "")
    .replace(/\[THINKING\][\s\S]*?\[\/THINKING\]/g, "")
    .replace(/\[\/?ANSWER\]/g, "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links and images keep their text
    .replace(/\[\d+\]/g, "") // citation markers
    .replace(/^\s{0,3}(#{1,6}\s+|[-*+]\s+|>\s?)/gm, "") // headings, bullets, quotes
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return text || null;
}

const ReportLink = styled.button(({ theme }) => ({
  all: "unset",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minHeight: 56,
  padding: 16,
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  background: theme.colors.sysBackgroundDefault,
  cursor: "pointer",
  transition: "background 0.1s ease",
  "&:hover": { background: theme.colors.sysBackgroundAlternative },
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysBorderDecorative}`,
    outlineOffset: -2,
  },
}));

const ReportHeader = styled.span({
  display: "flex",
  alignItems: "center",
  gap: 16,
  minHeight: 24,
  width: "100%",
});

const ReportPreview = styled.span({
  paddingLeft: 32,
  textAlign: "left",
});

const ReportName = styled.span(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  fontWeight: 600,
  color: theme.colors.sysTextSecondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
}));
